import React, { useState, useMemo } from 'react';
import {
  FaSearch,
  FaFilePdf,
  FaEye,
  FaDownload,
  FaCalendarAlt,
  FaBuilding
} from 'react-icons/fa';

/* ---------------- MOCK DATA ---------------- */

const companies = [
  { id: 1, name: 'Apple Inc.', ticker: 'AAPL' },
  { id: 2, name: 'Microsoft Corporation', ticker: 'MSFT' },
  { id: 3, name: 'Amazon.com Inc.', ticker: 'AMZN' },
];

const reports = [
  {
    id: 1,
    company: 'Apple Inc.',
    ticker: 'AAPL',
    type: 'annual',
    title: 'Annual Report 2023–24',
    size: '5.2 MB',
    date: '2024-03-15',
    url: 'https://www.apple.com/investor/pdf/Apple_10K_FY23.pdf'
  },
  {
    id: 2,
    company: 'Apple Inc.',
    ticker: 'AAPL',
    type: 'quarterly',
    title: 'Q4 Results 2024',
    size: '2.1 MB',
    date: '2024-01-25',
    url: 'https://www.apple.com/investor/pdf/Apple_Q1_2024_Press_Release.pdf'
  },
  {
    id: 3,
    company: 'Microsoft Corporation',
    ticker: 'MSFT',
    type: 'annual',
    title: 'Annual Report 2023',
    size: '6.1 MB',
    date: '2024-02-10',
    url: '#'
  }
];

/* ---------------- COMPONENT ---------------- */

const ViewPdfContent = () => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('annual');

  /* Filtered Data (Real-time behavior) */
  const filteredReports = useMemo(() => {
    return reports.filter(r =>
      r.type === activeTab &&
      (r.company.toLowerCase().includes(search.toLowerCase()) ||
        r.ticker.toLowerCase().includes(search.toLowerCase()))
    );
  }, [search, activeTab]);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">

      {/* -------- Header -------- */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Financial Document Repository
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Enterprise-grade access to annual and quarterly financial reports
        </p>
      </div>

      {/* -------- Global Search Bar -------- */}
      <div className="relative max-w-xl">
        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search by company name or ticker (e.g. AAPL)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700
                     bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                     focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
      </div>

      {/* -------- Tabs -------- */}
      <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700">
        {['annual', 'quarterly'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 font-medium transition-colors ${
              activeTab === tab
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'annual' ? 'Annual Reports' : 'Quarterly Reports'}
          </button>
        ))}
      </div>

      {/* -------- Data Table -------- */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Company</th>
              <th className="px-4 py-3 text-left">Report</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredReports.length > 0 ? (
              filteredReports.map((r, index) => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <td className="px-4 py-3">{index + 1}</td>
                  <td className="px-4 py-3 font-medium">
                    {r.company}
                    <div className="text-xs text-gray-500">{r.ticker}</div>
                  </td>
                  <td className="px-4 py-3 flex items-center gap-2">
                    <FaFilePdf className="text-red-500" />
                    {r.title}
                  </td>
                  <td className="px-4 py-3">
                    <FaCalendarAlt className="inline mr-1 text-gray-400" />
                    {r.date}
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <button
                      onClick={() => window.open(r.url, '_blank')}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <FaEye />
                    </button>
                    <button
                      onClick={() => window.open(r.url)}
                      className="px-3 py-1.5 border rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <FaDownload />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center py-10 text-gray-500">
                  No reports found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default ViewPdfContent;
